import franceLang from '@/assets/france-lang.png'
import englandLang from '@/assets/england-lang.png'
import sourethLang from '@/assets/soureth-lang.png'

export type ContentLanguage = 'fr' | 'en' | 'syr'

export interface TranslationData {
  title?: string | null
  description?: string | null
  content?: string | null
  soundcloudUrl?: string | null
  youtubeUrl?: string | null
  audioUrl?: string | null
  documentJson?: unknown
  documents?: unknown[] | null
  blocks?: unknown[] | null
}

export interface TranslatableItem {
  title?: string | null
  name?: string | null
  description?: string | null
  content?: string | null
  blocks?: unknown[] | null
  translations?: Partial<Record<'en' | 'syr' | 'argo', TranslationData>> | null
}

export interface LanguageOption {
  key: ContentLanguage | 'argo'
  label: string
  flag?: string
  glyph?: string
}

const LANGUAGE_OPTIONS: Record<LanguageOption['key'], Omit<LanguageOption, 'key'>> = {
  fr: { label: 'Français', flag: franceLang },
  en: { label: 'English', flag: englandLang },
  syr: { label: 'Soureth', flag: sourethLang },
  argo: { label: 'Araméen littéraire', glyph: 'ܐ' },
}

function hasValue(value: unknown): boolean {
  if (typeof value === 'string') return value.trim().length > 0
  if (typeof value === 'number' || typeof value === 'boolean') return true
  if (Array.isArray(value)) return value.some(hasValue)
  if (value && typeof value === 'object') return Object.values(value).some(hasValue)
  return false
}

function hasDocumentValue(value: unknown): boolean {
  if (typeof value === 'string') return value.trim().length > 0
  if (Array.isArray(value)) return value.some(hasDocumentValue)
  if (value && typeof value === 'object') {
    return Object.entries(value).some(([key, nestedValue]) => key !== 'type' && hasDocumentValue(nestedValue))
  }
  return false
}

function hasBlockContent(block: unknown): boolean {
  if (!block || typeof block !== 'object') return hasValue(block)
  const data = (block as { data?: Record<string, unknown> }).data
  if (!data) return false
  return Object.entries(data).some(([key, value]) => key === 'documentJson' ? hasDocumentValue(value) : hasValue(value))
}

/** A title alone is not considered a complete translated version. */
export function hasTranslationContent(translation?: TranslationData | null): boolean {
  if (!translation) return false

  return Boolean(
    hasValue(translation.description) ||
    hasValue(translation.content) ||
    hasValue(translation.soundcloudUrl) ||
    hasValue(translation.youtubeUrl) ||
    hasValue(translation.audioUrl) ||
    hasDocumentValue(translation.documentJson) ||
    translation.blocks?.some(hasBlockContent) ||
    hasValue(translation.documents),
  )
}

export function getSiteLanguage(language?: string): ContentLanguage {
  const normalized = language?.toLowerCase() || ''
  if (normalized.startsWith('en')) return 'en'
  if (normalized.startsWith('syr')) return 'syr'
  return 'fr'
}

export function getLocalizedTranslation(
  item: TranslatableItem,
  language?: string,
): TranslationData | undefined {
  const key = getSiteLanguage(language)
  if (key === 'fr') return undefined

  const translation = item.translations?.[key]
  return hasTranslationContent(translation) ? translation : undefined
}

export function getLocalizedTitle(item: TranslatableItem, language?: string): string {
  return getLocalizedTranslation(item, language)?.title?.trim() || item.title?.trim() || item.name?.trim() || ''
}

function getDocumentText(value: unknown): string | undefined {
  if (!value || typeof value !== 'object') return undefined

  if (Array.isArray(value)) {
    const text = value.map(getDocumentText).filter(Boolean).join(' ').trim()
    return text || undefined
  }

  const record = value as Record<string, unknown>
  if (typeof record.text === 'string' && record.text.trim()) return record.text

  const text = getDocumentText(record.content)
  return text || undefined
}

function getFirstTextBlock(blocks?: unknown[] | null): string | undefined {
  for (const block of blocks || []) {
    if (!block || typeof block !== 'object') continue
    const data = (block as { data?: { text?: unknown; documentJson?: unknown } }).data
    if (typeof data?.text === 'string' && data.text.trim()) return data.text
    const documentText = getDocumentText(data?.documentJson)
    if (documentText) return documentText
  }
  return undefined
}

export function getLocalizedDescription(item: TranslatableItem, language?: string): string {
  const translation = getLocalizedTranslation(item, language)
  return (
    translation?.description?.trim() ||
    translation?.content?.trim() ||
    getFirstTextBlock(translation?.blocks) ||
    item.description?.trim() ||
    item.content?.trim() ||
    getFirstTextBlock(item.blocks) ||
    ''
  )
}

export function getAvailableLanguages(item: TranslatableItem): LanguageOption[] {
  return (['fr', 'en', 'syr', 'argo'] as const)
    .filter(key => key === 'fr' || hasTranslationContent(item.translations?.[key]))
    .map(key => ({ key, ...LANGUAGE_OPTIONS[key] }))
}
